/**
 * arXiv Firehose — recent cs.AI / cs.CL / cs.LG submissions, raw + unfiltered.
 *
 * Use Trending Papers for the curated upvote-driven subset; this page is the
 * full unfiltered feed. Refreshes every 4h.
 *
 * Data: /api/frontier/arxiv
 */
import { useEffect, useMemo, useState } from 'react'
import { BookOpen, RefreshCw, ExternalLink, Filter, Search } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface ArxivPaper {
  id: string
  title: string
  summary: string
  authors: string[]
  published: string
  updated: string
  primary_cat: string
  categories: string[]
  abs_url: string
  pdf_url: string
}

const CAT_TONE: Record<string, string> = {
  'cs.AI': 'bg-purple/15 text-purple-light',
  'cs.CL': 'bg-blue/15 text-blue-light',
  'cs.LG': 'bg-emerald/15 text-emerald',
  'cs.CR': 'bg-rose/15 text-rose-light',
  'cs.CV': 'bg-amber/15 text-amber-light',
}

export default function ArxivFirehosePage() {
  const [papers, setPapers] = useState<ArxivPaper[]>([])
  const [fetchedAt, setFetchedAt] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [catFilter, setCatFilter] = useState('')
  const [search, setSearch] = useState('')

  const load = (refresh = false) => {
    setLoading(true)
    api.get('/api/frontier/arxiv', { params: { refresh, max_results: 80 } })
      .then(r => {
        setPapers(r.data?.papers || [])
        setFetchedAt(r.data?.fetched_at || null)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }
  useEffect(() => { load(false) }, [])

  const allCats = useMemo(() => {
    const s = new Set<string>()
    papers.forEach(p => p.categories?.forEach(c => s.add(c)))
    return Array.from(s).sort()
  }, [papers])

  const filtered = useMemo(() => papers
    .filter(p =>
      (!catFilter || p.categories?.includes(catFilter)) &&
      (!search   || `${p.title} ${p.authors.join(' ')}`.toLowerCase().includes(search.toLowerCase()))
    ),
  [papers, catFilter, search])

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-4 pb-2 border-b border-border flex-wrap">
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <BookOpen size={18} className="text-emerald" /> ARXIV FIREHOSE
        </h1>
        <span className="text-[11px] uppercase tracking-widest text-text-muted">
          cs.AI · cs.CL · cs.LG · raw and unfiltered
        </span>
        {fetchedAt && (
          <span className="text-[10px] text-text-muted font-mono">
            updated {fetchedAt.slice(0, 16).replace('T', ' ')} UTC
          </span>
        )}
        <button type="button" onClick={() => load(true)} disabled={loading} className="btn-ghost ml-auto">
          <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Force refresh
        </button>
      </div>

      <div className="flex items-center gap-2 text-xs flex-wrap">
        <Filter size={12} className="text-text-muted" />
        <select className="input" value={catFilter} onChange={e => setCatFilter(e.target.value)}
          aria-label="Filter by category">
          <option value="">All cats ({papers.length})</option>
          {allCats.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <div className="relative flex-1 min-w-[200px]">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-text-muted" />
          <input className="input pl-7 w-full" placeholder="Search title / author"
            value={search} onChange={e => setSearch(e.target.value)} aria-label="Search papers" />
        </div>
        <span className="text-text-muted">{filtered.length} of {papers.length}</span>
      </div>

      <div className="space-y-2">
        {filtered.map(p => (
          <article key={p.id} className="card p-3">
            <div className="flex items-start gap-2 flex-wrap">
              <span className="text-[10px] font-mono text-amber-light shrink-0">
                {(p.published || '').slice(0, 10)}
              </span>
              <span className="text-[10px] font-mono text-text-muted shrink-0">{p.id}</span>
              <h3 className="text-sm font-semibold text-text-primary flex-1 min-w-[40%]">
                <a href={p.abs_url} target="_blank" rel="noreferrer" className="hover:text-blue-light">
                  {p.title}
                </a>
              </h3>
              <div className="flex flex-wrap gap-1">
                {(p.categories || []).slice(0, 4).map(c => (
                  <span key={c} className={clsx('badge text-[9px]', CAT_TONE[c] || 'bg-surface-raised text-text-muted')}>
                    {c}
                  </span>
                ))}
              </div>
            </div>
            {p.authors.length > 0 && (
              <div className="text-[10px] text-text-muted mt-1">
                {p.authors.slice(0, 4).join(', ')}{p.authors.length > 4 ? ` +${p.authors.length - 4}` : ''}
              </div>
            )}
            <p className="text-xs text-text-secondary leading-relaxed mt-1.5 line-clamp-3">{p.summary}</p>
            <div className="flex items-center gap-3 mt-1.5 text-[10px]">
              <a href={p.abs_url} target="_blank" rel="noreferrer"
                className="text-blue-light hover:underline inline-flex items-center gap-1">
                abstract <ExternalLink size={9} />
              </a>
              {p.pdf_url && (
                <a href={p.pdf_url} target="_blank" rel="noreferrer"
                  className="text-emerald hover:underline">
                  PDF
                </a>
              )}
            </div>
          </article>
        ))}
      </div>

      <p className="text-[10px] text-text-muted text-center">
        Source: <a href="https://arxiv.org/list/cs.AI/recent" target="_blank" rel="noreferrer"
          className="text-blue-light hover:underline">arXiv cs.AI / cs.CL / cs.LG</a> via export.arxiv.org · refreshed every 4h.
      </p>
    </div>
  )
}
